import { cur } from '@/lib/currency';
import { VatTreatmentFields, vatPayload, vatError } from '@/components/VatTreatmentFields';
import { useEffect, useState, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Plus, Trash2, Receipt, DollarSign, Banknote, CreditCard, FileText, Building2, RotateCcw, Upload, Image as ImageIcon, Eye, AlertTriangle, Copy } from 'lucide-react';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import api from '@/lib/api';
import { SupplierPaymentJournalDialog } from '@/components/suppliers/SupplierPaymentJournalDialog';
import { useBranchStore } from '@/stores';
import PickFromBankDialog from '@/components/banking/PickFromBankDialog';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { ExportButtons } from '@/components/ExportButtons';
import { DateFilter } from '@/components/DateFilter';
import { BranchFilter } from '@/components/BranchFilter';
import { useLanguage } from '@/contexts/LanguageContext';
import { SearchableSelect } from '@/components/SearchableSelect';
import { DuplicatePeekList } from '@/components/common/DuplicatePeekList';
import { localToday } from '@/lib/dateRange';

export default function SupplierPaymentsPage() {
  const { t } = useLanguage();
  const [payments, setPayments] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const { branches, fetchBranches: _fetchBr } = useBranchStore();
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [showForm, setShowForm] = useState(false);
  const [formType, setFormType] = useState('payment'); // 'payment' or 'bill'
  const [formData, setFormData] = useState({
    supplier_id: '',
    amount: '',
    payment_mode: 'cash',
    branch_id: '',
    expense_for_branch_id: '',
    date: localToday(),
    notes: '',
    category: 'Supplier Purchase',
    apply_wht: false,
    wht_rate_id: '',
    wht_rate_value: 0,
  });
  const [whtRates, setWhtRates] = useState([]);
  // iter442e — bank-txn picker state
  const [showPickBank, setShowPickBank] = useState(false);
  const [pickedBankTxn, setPickedBankTxn] = useState(null);
  const [dateFilter, setDateFilter] = useState({ start: null, end: null, period: 'all' });
  const [branchFilter, setBranchFilter] = useState([]);
  const [showReturnForm, setShowReturnForm] = useState(false);
  const [returns, setReturns] = useState([]);
  const [returnData, setReturnData] = useState({
    supplier_id: '', amount: '', return_type: 'credit_return', reason: '', invoice_ref: '',
    branch_id: '', date: localToday(), bill_image_url: '',
  });
  const [uploading, setUploading] = useState(false);
  const [billUploading, setBillUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showDuplicateWarning, setShowDuplicateWarning] = useState(false);
  const [duplicateCount, setDuplicateCount] = useState(0);
  const [duplicateExisting, setDuplicateExisting] = useState([]);
  const [pendingSubmitType, setPendingSubmitType] = useState(null);
  // iter592 — Inline supplier reassignment for legacy "— (unknown)" rows.
  // Lets the admin fix a single misstamped row from the UI instead of
  // running the full Data Hygiene migration.
  const [reassignFor, setReassignFor] = useState(null); // payment object or null
  const [journalFor, setJournalFor] = useState(null);
  const [reassignTarget, setReassignTarget] = useState(''); // chosen supplier_id
  const [reassignSubmitting, setReassignSubmitting] = useState(false);
  const billFileRef = useRef(null);
  const returnBillRef = useRef(null);

  // Credit-invoice linking for the Payment form
  const [unpaidInvoices, setUnpaidInvoices] = useState([]);
  const [invoicesLoading, setInvoicesLoading] = useState(false);
  const [linkedInvoiceIds, setLinkedInvoiceIds] = useState([]);

  // When supplier changes on the Payment form, refresh the unpaid-invoice list
  useEffect(() => {
    setLinkedInvoiceIds([]);
    if (!formData.supplier_id || formType !== 'payment' || !showForm) {
      setUnpaidInvoices([]);
      return;
    }
    setInvoicesLoading(true);
    api.get(`/suppliers/${formData.supplier_id}/unpaid-invoices`)
      .then(r => setUnpaidInvoices(r.data?.invoices || []))
      .catch(() => setUnpaidInvoices([]))
      .finally(() => setInvoicesLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.supplier_id, formType, showForm]);

  const totalLinkedAmount = unpaidInvoices
    .filter(inv => linkedInvoiceIds.includes(inv.invoice_id))
    .reduce((s, inv) => s + inv.unpaid, 0);

  useEffect(() => { fetchData(); }, []);

  // iter431 — Focus / pulse-highlight a payment row when arriving with ?focus=<id>.
  const [searchParams, setSearchParams] = useSearchParams();
  const urlFocus = searchParams.get('focus');
  useEffect(() => {
    if (!urlFocus || !payments?.length) return;
    if (!payments.some(p => p.id === urlFocus)) return;
    const t = setTimeout(() => {
      const el = document.querySelector(`[data-testid="payment-row-${urlFocus}"]`);
      if (el) {
        try { el.scrollIntoView({ behavior: 'smooth', block: 'center' }); } catch { /* noop */ }
        el.classList.add('row-focus-pulse');
        setTimeout(() => {
          el.classList.remove('row-focus-pulse');
          const sp = new URLSearchParams(searchParams);
          sp.delete('focus');
          setSearchParams(sp, { replace: true });
        }, 2800);
      }
    }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlFocus, payments]);

  const fetchData = async (page = 1) => {
    try {
      const [paymentsRes, suppliersRes, branchesRes, returnsRes] = await Promise.all([
        api.get(`/supplier-payments?page=${page}&limit=100`),
        api.get('/suppliers'),
        Promise.resolve({ data: [] }),
        api.get('/supplier-returns').catch(() => ({ data: [] })),
      ]);
      const payData = paymentsRes.data;
      setPayments(payData.data || payData);
      setTotalPages(payData.pages || 1);
      setTotalRecords(payData.total || 0);
      setCurrentPage(payData.page || 1);
      setSuppliers(suppliersRes.data);
      setReturns(returnsRes.data);
    } catch { toast.error('Failed to fetch data'); }
    finally { setLoading(false); }
  };

  const handlePaymentSubmit = async (e) => {
    e.preventDefault();
    if (!formData.supplier_id) { toast.error('Please select a supplier'); return; }

    // Check for duplicates before saving
    const amt = parseFloat(formData.amount);
    if (formData.supplier_id && amt > 0) {
      try {
        const checkRes = await api.get(`/supplier-payments/check-duplicate?supplier_id=${formData.supplier_id}&amount=${amt}&date=${formData.date}`);
        if (checkRes.data?.has_duplicate) {
          setDuplicateCount(checkRes.data.count);
          setDuplicateExisting(checkRes.data.existing || []);
          setPendingSubmitType('payment');
          setShowDuplicateWarning(true);
          return;
        }
      } catch { /* proceed if check fails */ }
    }

    await submitPayment();
  };

  const submitPayment = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      // iter442e — Fast path: if user picked a real bank-statement txn,
      // use the bulk-from-stmt-txns endpoint which creates the supplier
      // payment AND marks the statement line as matched in one shot.
      //
      // iter724 — Bug fix for "credit payment not reducing supplier
      // balance" (user reported twice). The fast-path used to omit
      // `paying_bank_account_id`, but `bank_payments.create_bank_payment`
      // requires it when `payment_mode='bank'` and silently HTTP-400s
      // the row into the `skipped` list. Worse, this code didn't
      // inspect `created_count` so it showed a "Payment recorded"
      // success toast even when zero rows were actually created. We
      // now (a) pass `paying_bank_account_id` from the picked txn —
      // the field is already returned by the bank-line lister — and
      // (b) validate `created_count > 0` before declaring success,
      // surfacing the real backend error when it's not.
      if (formData.payment_mode === 'bank' && pickedBankTxn && formData.supplier_id) {
        const res = await api.post('/banking/bank-payments/bulk-from-stmt-txns', {
          txn_refs: [{ statement_id: pickedBankTxn.statement_id, txn_index: pickedBankTxn.txn_index }],
          payee_type: 'supplier',
          payee_id: formData.supplier_id,
          paying_branch_id: formData.branch_id || null,
          // iter724 — REQUIRED for the backend (was missing).
          paying_bank_account_id: pickedBankTxn.bank_account_id || null,
          description: formData.notes || `Payment to ${suppliers.find(s => s.id === formData.supplier_id)?.name || 'supplier'}`,
        });
        const createdCount = Number(res?.data?.created_count || 0);
        const skippedCount = Number(res?.data?.skipped_count || 0);
        if (createdCount === 0) {
          // iter724 — Surface the real reason instead of a fake success.
          const reason = res?.data?.skipped?.[0]?.reason || 'unknown reason';
          toast.error(`Payment NOT recorded — ${reason}`);
          return;
        }
        if (skippedCount > 0) {
          toast.warning(`Payment recorded, but ${skippedCount} line skipped: ${res?.data?.skipped?.[0]?.reason || ''}`);
        } else {
          toast.success('Payment recorded & linked to bank statement');
        }
        setShowForm(false);
        setPickedBankTxn(null);
        resetForm();
        fetchData();
        return;
      }
      const payload = {
        supplier_id: formData.supplier_id,
        amount: parseFloat(formData.amount),
        payment_mode: formData.payment_mode,
        branch_id: formData.branch_id || null,
        expense_for_branch_id: formData.expense_for_branch_id || null,
        date: `${formData.date}T${new Date().toTimeString().slice(0,8)}`,
        notes: formData.notes,
        linked_invoice_ids: linkedInvoiceIds.length ? linkedInvoiceIds : null,
      };
      if (formData.apply_wht && formData.wht_rate_id) {
        payload.wht_rate_id = formData.wht_rate_id;
      }
      const spRes = await api.post('/supplier-payments', payload);
      
      // Also record as expense so it appears in expense reports (linked → no second journal)
      await api.post('/expenses', {
        amount: parseFloat(formData.amount),
        category: 'Supplier Payment',
        description: `Payment to ${suppliers.find(s => s.id === formData.supplier_id)?.name || 'supplier'} - ${formData.notes || 'Credit payment'}`,
        payment_mode: formData.payment_mode,
        supplier_id: formData.supplier_id,
        branch_id: formData.branch_id || '',
        expense_for_branch_id: formData.expense_for_branch_id || '',
        date: `${formData.date}T${new Date().toTimeString().slice(0,8)}`,
        ...(formData.payment_mode !== 'credit' && spRes?.data?.id ? { supplier_payment_id: spRes.data.id } : {}),
      }).catch(() => {}); // Don't fail if expense recording fails
      
      toast.success('Payment recorded successfully');
      setShowForm(false);
      resetForm();
      fetchData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to add payment'); }
    finally { setSubmitting(false); }
  };

  const handleBillSubmit = async (e) => {
    e.preventDefault();
    if (!formData.supplier_id) { toast.error('Please select a supplier'); return; }

    // Check for duplicates
    const amt = parseFloat(formData.amount);
    if (formData.supplier_id && amt > 0) {
      try {
        const checkRes = await api.get(`/supplier-payments/check-duplicate?supplier_id=${formData.supplier_id}&amount=${amt}&date=${formData.date}`);
        if (checkRes.data?.has_duplicate) {
          setDuplicateCount(checkRes.data.count);
          setDuplicateExisting(checkRes.data.existing || []);
          setPendingSubmitType('bill');
          setShowDuplicateWarning(true);
          return;
        }
      } catch { /* proceed if check fails */ }
    }

    await submitBill();
  };

  const submitBill = async () => {
    if (submitting) return;
    const _ve = vatError(formData);
    if (_ve) { toast.error(_ve); return; }
    setSubmitting(true);
    try {
      const supplierName = suppliers.find(s => s.id === formData.supplier_id)?.name || 'supplier';
      await api.post('/expenses', {
        amount: parseFloat(formData.amount),
        category: formData.category || 'Supplier Purchase',
        description: formData.notes || `Purchase from ${supplierName}`,
        payment_mode: formData.payment_mode,
        supplier_id: formData.supplier_id,
        branch_id: formData.branch_id || '',
        expense_for_branch_id: formData.expense_for_branch_id || '',
        date: `${formData.date}T${new Date().toTimeString().slice(0,8)}`,
        ...vatPayload(formData),
      });
      
      // Also create supplier payment to update credit balance
      await api.post('/supplier-payments', {
        supplier_id: formData.supplier_id,
        amount: parseFloat(formData.amount),
        payment_mode: formData.payment_mode,
        branch_id: formData.branch_id || null,
        date: `${formData.date}T${new Date().toTimeString().slice(0,8)}`,
        notes: `Bill: ${formData.notes || `Purchase from ${supplierName}`}`,
      }).catch(() => {}); // Don't fail if payment record fails
      
      const modeText = formData.payment_mode === 'credit'
        ? 'Added to supplier credit balance'
        : 'Paid immediately (no balance change)';
      toast.success(`Purchase bill recorded! ${modeText}`);
      setShowForm(false);
      resetForm();
      fetchData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to add bill'); }
    finally { setSubmitting(false); }
  };

  const resetForm = () => {
    setFormData({
      supplier_id: '', amount: '', payment_mode: formType === 'bill' ? 'credit' : 'cash',
      branch_id: '', expense_for_branch_id: '', date: localToday(), notes: '', category: 'Supplier Purchase',
    });
  };

  const handleBillUpload = async (e, target = 'form') => {
    const file = e.target.files[0];
    if (!file) return;
    setBillUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post('/supplier-payments/upload-bill', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      if (target === 'form') setFormData(d => ({ ...d, bill_image_url: res.data.bill_url }));
      else setReturnData(d => ({ ...d, bill_image_url: res.data.bill_url }));
      toast.success('Bill image uploaded');
    } catch { toast.error('Upload failed'); }
    finally { setBillUploading(false); }
  };

  const handleReturnSubmit = async (e) => {
    e.preventDefault();
    if (!returnData.supplier_id) { toast.error('Select a supplier'); return; }
    if (submitting) return;
    setSubmitting(true);
    try {
      await api.post('/supplier-returns', {
        ...returnData,
        amount: parseFloat(returnData.amount),
        date: new Date(returnData.date).toISOString(),
      });
      const typeLabel = returnData.return_type === 'cash_refund' ? 'Cash refund' : returnData.return_type === 'credit_return' ? 'Credit return' : 'Full invoice return';
      toast.success(`${typeLabel} recorded for ${cur()} ${returnData.amount}`);
      setShowReturnForm(false);
      setReturnData({ supplier_id: '', amount: '', return_type: 'credit_return', reason: '', invoice_ref: '', branch_id: '', date: localToday(), bill_image_url: '' });
      fetchData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to record return'); }
    finally { setSubmitting(false); }
  };

  const handleDeleteReturn = async (id) => {
    if (window.confirm('Delete this return? Balance changes will be reversed.')) {
      try {
        await api.delete(`/supplier-returns/${id}`);
        toast.success('Return deleted and balance reversed');
        fetchData();
      } catch { toast.error('Failed to delete'); }
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Delete this payment? Supplier balance will be updated.')) {
      try {
        await api.delete(`/supplier-payments/${id}`);
        toast.success('Payment deleted and balance updated');
        fetchData();
      } catch (error) { toast.error(error.response?.data?.detail || 'Failed to delete'); }
    }
  };

  // iter592 — Reassign a legacy "— (unknown)" row to the right supplier.
  // Backend recomputes current_credit for both the previous (if any)
  // and the new supplier so no card is left stale.
  const handleReassignSubmit = async () => {
    if (!reassignFor?.id || !reassignTarget) return;
    setReassignSubmitting(true);
    try {
      await api.patch(`/supplier-payments/${reassignFor.id}/reassign-supplier`, {
        supplier_id: reassignTarget,
      });
      toast.success('Supplier reassigned · credit balances updated');
      setReassignFor(null);
      setReassignTarget('');
      fetchData(currentPage);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reassign');
    } finally {
      setReassignSubmitting(false);
    }
  };

  const openForm = (type) => {
    setFormType(type);
    setFormData({
      supplier_id: '', amount: '', payment_mode: type === 'bill' ? 'credit' : 'cash',
      branch_id: '', expense_for_branch_id: '', date: localToday(), notes: '', category: 'Supplier Purchase',
    });
    setShowForm(true);
  };

  const selectedSupplier = suppliers.find(s => s.id === formData.supplier_id);

  const filteredPayments = payments.filter(p => {
    if (branchFilter.length > 0 && !branchFilter.includes(p.branch_id)) return false;
    if (dateFilter.start && dateFilter.end) {
      const d = new Date(p.date);
      return d >= new Date(dateFilter.start) && d <= new Date(dateFilter.end + 'T23:59:59');
    }
    return true;
  });

  const filteredReturns = returns.filter(r => {
    if (branchFilter.length > 0 && !branchFilter.includes(r.branch_id)) return false;
    if (dateFilter.start && dateFilter.end) {
      const d = new Date(r.date);
      return d >= new Date(dateFilter.start) && d <= new Date(dateFilter.end + 'T23:59:59');
    }
    return true;
  });

  // Detect duplicates: same supplier + same amount on same day
  const spDuplicateIds = new Set();
  (() => {
    const dupeKeys = {};
    filteredPayments.forEach(p => {
      const dateKey = p.date ? p.date.substring(0, 10) : 'unknown';
      const key = `${dateKey}_${p.supplier_id || 'none'}_${(p.amount || 0).toFixed(2)}`;
      if (!dupeKeys[key]) dupeKeys[key] = [];
      dupeKeys[key].push(p.id);
    });
    Object.values(dupeKeys).forEach(ids => {
      if (ids.length > 1) ids.forEach(id => spDuplicateIds.add(id));
    });
  })();

  if (loading) {
    return (<DashboardLayout><div className="flex items-center justify-center h-64">Loading...</div></DashboardLayout>);
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-emerald-600 to-teal-700 flex items-center justify-center shadow-lg shadow-emerald-500/20">
              <Receipt size={24} className="text-white" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold font-outfit tracking-tight text-foreground" data-testid="supplier-payments-page-title">Supplier Payments</h1>
              <p className="text-sm text-muted-foreground mt-0.5">Record payments, purchase bills, and track supplier transactions</p>
            </div>
          </div>
          <div className="flex gap-2 items-center flex-wrap">
            <BranchFilter onChange={setBranchFilter} />
            <DateFilter onFilterChange={setDateFilter} />
            <ExportButtons branchIds={branchFilter} dataType="supplier-payments" startDate={dateFilter?.start} endDate={dateFilter?.end} />
            <Button onClick={() => openForm('bill')} variant="outline" data-testid="add-bill-button"
              className="rounded-full text-warning border-warning/30 hover:bg-warning/10">
              <Receipt size={16} className="mr-1" /> Add Bill
            </Button>
            <Button onClick={() => setShowReturnForm(true)} variant="outline" data-testid="add-return-button"
              className="rounded-full text-error border-error/30 hover:bg-error/10">
              <RotateCcw size={16} className="mr-1" /> Return
            </Button>
            <Button onClick={() => openForm('payment')} data-testid="add-payment-button" className="rounded-full">
              <DollarSign size={16} className="mr-1" /> Pay Credit
            </Button>
          </div>
        </div>

        {/* Form Card */}
        {showForm && (
          <Card className="border-2 border-primary/20" data-testid="payment-form-card">
            <CardHeader className="pb-3">
              <CardTitle className="font-outfit flex items-center gap-2">
                {formType === 'bill' ? (
                  <><Receipt className="text-warning" size={20} /> Add Purchase Bill</>
                ) : (
                  <><DollarSign className="text-info" size={20} /> Pay Supplier Credit</>
                )}
              </CardTitle>
              {formType === 'bill' ? (
                <p className="text-sm text-warning bg-warning/10 p-2 rounded-lg border border-warning/30 mt-2">
                  Record a purchase from a supplier. Choose <strong>Credit</strong> to pay later (adds to balance), or <strong>Cash/Bank</strong> if paid now. All bills are recorded as expenses automatically.
                </p>
              ) : (
                <p className="text-sm text-info bg-info/10 p-2 rounded-lg border border-info/30 mt-2">
                  Make a payment to reduce a supplier's outstanding credit balance. This will also be recorded as an expense.
                </p>
              )}
            </CardHeader>
            <CardContent>
              <form onSubmit={formType === 'bill' ? handleBillSubmit : handlePaymentSubmit}>
                <div className="space-y-4">
                  {/* Supplier Selection - Searchable */}
                  <div>
                    <Label>Supplier *</Label>
                    <SearchableSelect
                      items={suppliers}
                      value={formData.supplier_id}
                      onChange={(id) => {
                        // iter587 — Bug fix: never overwrite a branch the
                        // user already picked. The previous behaviour
                        // silently reset branch_id to the supplier's
                        // default whenever a supplier was selected after
                        // the branch, causing payments to save against
                        // the wrong branch (user picked LV → saved as
                        // supplier's default ISL).
                        const sup = suppliers.find(s => s.id === id);
                        setFormData({...formData, supplier_id: id, branch_id: formData.branch_id || sup?.branch_id || ''});
                      }}
                      placeholder="Search supplier by name..."
                      className="mt-2"
                    />
                    {selectedSupplier && (
                      <div className="mt-2 p-2 bg-muted/50 rounded-lg text-xs text-muted-foreground flex gap-4">
                        <span>Credit: <strong className="text-error">{cur()} {selectedSupplier.current_credit?.toFixed(2) || '0.00'}</strong></span>
                        <span>Limit: <strong>{cur()} {selectedSupplier.credit_limit?.toFixed(2) || '0.00'}</strong></span>
                        <span>Total Purchases: <strong className="text-warning">{cur()} {(selectedSupplier.total_purchases || 0).toFixed(2)}</strong></span>
                      </div>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label>Amount (SR) *</Label>
                      <Input type="number" step="0.01" data-testid="amount-input"
                        value={formData.amount}
                        onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                        required placeholder="0.00" className="text-lg font-bold" />
                    </div>

                    <div>
                      <Label>Payment Mode *</Label>
                      {formType === 'bill' ? (
                        <div className="flex gap-2 mt-1">
                          {['credit', 'cash', 'bank'].map(mode => (
                            <button key={mode} type="button" onClick={() => setFormData({ ...formData, payment_mode: mode })}
                              data-testid={`mode-${mode}`}
                              className={`flex-1 py-2.5 px-3 rounded-lg text-sm font-medium transition-all border ${
                                formData.payment_mode === mode
                                  ? mode === 'credit' ? 'bg-warning text-white border-warning'
                                    : mode === 'cash' ? 'bg-success text-white border-success'
                                    : 'bg-info text-white border-info'
                                  : 'bg-white text-muted-foreground border-border hover:bg-muted/50'
                              }`}>
                              {mode === 'credit' && <Receipt size={14} className="inline mr-1" />}
                              {mode === 'cash' && <Banknote size={14} className="inline mr-1" />}
                              {mode === 'bank' && <CreditCard size={14} className="inline mr-1" />}
                              {mode.charAt(0).toUpperCase() + mode.slice(1)}
                            </button>
                          ))}
                        </div>
                      ) : (
                        <Select value={formData.payment_mode} onValueChange={(val) => setFormData({ ...formData, payment_mode: val })}>
                          <SelectTrigger data-testid="payment-mode-select"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="cash"><div className="flex items-center gap-2"><Banknote size={14} /> Cash</div></SelectItem>
                            <SelectItem value="bank"><div className="flex items-center gap-2"><CreditCard size={14} /> Bank Transfer</div></SelectItem>
                          </SelectContent>
                        </Select>
                      )}
                      <p className="text-xs text-muted-foreground mt-1">
                        {formType === 'bill'
                          ? (formData.payment_mode === 'credit' ? 'Will add to supplier balance (pay later)' : 'Paid now - no balance change')
                          : 'Payment will reduce supplier credit balance'}
                      </p>
                      {/* iter442e — Bank-statement picker (only when paying by bank) */}
                      {formData.payment_mode === 'bank' && (
                        <div className="mt-1.5">
                          {pickedBankTxn ? (
                            <div className="rounded-md border border-info/30 bg-info/5 px-2 py-1.5 text-[11px] flex items-center justify-between gap-2"
                              data-testid="sp-picked-bank-txn">
                              <div className="min-w-0 flex-1">
                                <div className="font-medium truncate">{pickedBankTxn.description}</div>
                                <div className="text-muted-foreground text-[10px]">{pickedBankTxn.date} · {pickedBankTxn.bank_name || 'Bank'} · {cur()} {pickedBankTxn.amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}</div>
                              </div>
                              <button type="button"
                                onClick={() => setPickedBankTxn(null)}
                                className="text-error hover:underline text-[10px]"
                                data-testid="sp-clear-bank-txn">
                                Clear
                              </button>
                            </div>
                          ) : (
                            <button type="button"
                              onClick={() => setShowPickBank(true)}
                              disabled={!formData.supplier_id}
                              className={`text-[11px] inline-flex items-center gap-1 ${formData.supplier_id ? 'text-info hover:underline' : 'text-muted-foreground cursor-not-allowed'}`}
                              data-testid="sp-pick-bank-btn">
                              <Banknote size={11} /> {formData.supplier_id ? 'Pick from bank statement…' : 'Pick a supplier first to enable bank picker'}
                            </button>
                          )}
                        </div>
                      )}
                    </div>

                    <div>
                      <Label>Paid By (Branch)</Label>
                      <Select value={formData.branch_id || "all"} onValueChange={(val) => setFormData({ ...formData, branch_id: val === "all" ? "" : val })}>
                        <SelectTrigger data-testid="sp-branch-select"><SelectValue placeholder="Select branch" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Branches</SelectItem>
                          {branches.map((branch) => (
                            <SelectItem key={branch.id} value={branch.id}>{branch.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label>Expense For (Branch)</Label>
                      <Select value={formData.expense_for_branch_id || "none"} onValueChange={(val) => setFormData({ ...formData, expense_for_branch_id: val === "none" ? "" : val })}>
                        <SelectTrigger data-testid="sp-expense-for-branch"><SelectValue placeholder="Same as Paid By" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Same as Paid By</SelectItem>
                          {branches.filter(b => b.id !== formData.branch_id).map((branch) => (
                            <SelectItem key={branch.id} value={branch.id}>{branch.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-muted-foreground mt-0.5">Select if a different branch bears this expense</p>
                    </div>

                    <div>
                      <Label>Date *</Label>
                      <Input type="date" data-testid="date-input" value={formData.date}
                        onChange={(e) => setFormData({ ...formData, date: e.target.value })} required />
                    </div>

                    {formType === 'bill' && (
                      <div>
                        <Label>Category</Label>
                        <Select value={formData.category || "Supplier Purchase"} onValueChange={(v) => setFormData({ ...formData, category: v })}>
                          <SelectTrigger data-testid="bill-category-select"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Supplier Purchase">Supplier Purchase</SelectItem>
                            <SelectItem value="Inventory">Inventory</SelectItem>
                            <SelectItem value="Raw Materials">Raw Materials</SelectItem>
                            <SelectItem value="Supplies">Supplies</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    )}

                    {/* Credit invoice linking — only for the Payment form */}
                    {formType === 'payment' && formData.supplier_id && (
                      <div className="md:col-span-2 mt-1 border border-border rounded-xl p-3 bg-muted/30" data-testid="invoice-linker">
                        <div className="flex items-center justify-between mb-2">
                          <Label className="text-sm font-semibold flex items-center gap-1.5">
                            <FileText size={14} /> Apply to credit invoices
                          </Label>
                          {unpaidInvoices.length > 0 && (
                            <span className="text-[11px] text-muted-foreground">
                              {linkedInvoiceIds.length} of {unpaidInvoices.length} selected · Covers {cur()} {totalLinkedAmount.toFixed(2)}
                            </span>
                          )}
                        </div>
                        {invoicesLoading ? (
                          <p className="text-xs text-muted-foreground py-2">Loading unpaid invoices…</p>
                        ) : unpaidInvoices.length === 0 ? (
                          <p className="text-xs text-muted-foreground italic py-1">No unpaid credit invoices for this supplier. Payment will be applied to supplier credit balance.</p>
                        ) : (
                          <div className="space-y-1 max-h-48 overflow-y-auto">
                            {unpaidInvoices.map(inv => {
                              const checked = linkedInvoiceIds.includes(inv.invoice_id);
                              return (
                                <label
                                  key={inv.invoice_id}
                                  className={`flex items-center gap-2 px-2 py-1.5 rounded-lg text-xs cursor-pointer border ${checked ? 'bg-info/10 border-info/40' : 'border-transparent hover:bg-muted/50'}`}
                                  data-testid={`invoice-row-${inv.invoice_id}`}
                                >
                                  <input
                                    type="checkbox" checked={checked}
                                    onChange={() => setLinkedInvoiceIds(
                                      checked ? linkedInvoiceIds.filter(x => x !== inv.invoice_id) : [...linkedInvoiceIds, inv.invoice_id]
                                    )}
                                    className="w-4 h-4 accent-info"
                                  />
                                  <span className="font-mono text-[10px] text-muted-foreground w-20 truncate">{inv.invoice_number || inv.invoice_id.slice(0, 8)}</span>
                                  <span className="text-muted-foreground w-20 shrink-0">{inv.date ? format(new Date(inv.date), 'dd MMM yy') : '-'}</span>
                                  <span className="flex-1 truncate text-foreground">{inv.description || '-'}</span>
                                  <span className="font-mono text-[11px] text-warning font-medium w-24 text-right">{cur()} {inv.unpaid.toFixed(2)}</span>
                                </label>
                              );
                            })}
                          </div>
                        )}
                        {unpaidInvoices.length > 0 && (
                          <div className="flex items-center justify-between mt-2 pt-2 border-t border-border">
                            <button
                              type="button" className="text-[11px] text-info font-medium hover:underline"
                              onClick={() => {
                                // Auto-pick oldest invoices up to the entered amount (FIFO)
                                const amt = parseFloat(formData.amount) || 0;
                                if (!amt) { toast.info('Enter payment amount first'); return; }
                                let remaining = amt;
                                const picked = [];
                                for (const inv of unpaidInvoices) {
                                  if (remaining <= 0.01) break;
                                  picked.push(inv.invoice_id);
                                  remaining -= inv.unpaid;
                                }
                                setLinkedInvoiceIds(picked);
                              }}
                              data-testid="auto-fill-linked-btn"
                            >Auto-fill by amount (oldest first)</button>
                            {linkedInvoiceIds.length > 0 && (
                              <button type="button" className="text-[11px] text-muted-foreground hover:underline" onClick={() => setLinkedInvoiceIds([])}>Clear</button>
                            )}
                          </div>
                        )}
                      </div>
                    )}

                    <div className={formType === 'bill' ? '' : 'md:col-span-2'}>
                      <Label>Notes / Invoice #{formType === 'payment' ? '' : ' (Optional)'}</Label>
                      <Textarea data-testid="notes-input" value={formData.notes}
                        onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                        placeholder={formType === 'bill' ? 'e.g., Invoice #1234 - Chicken, Rice, etc.' : 'Payment reference / notes'} />
                    </div>
                    {formType === 'bill' && (
                      <div className="md:col-span-2">
                        <VatTreatmentFields value={formData} amount={formData.amount} onChange={(p) => setFormData((f) => ({ ...f, ...p }))} testPrefix="sp-bill-vat" />
                      </div>
                    )}

                    {/* Bill Image Upload */}
                    <div className="md:col-span-2">
                      <Label>Bill / Invoice Image</Label>
                      <div className="flex items-center gap-3 mt-1">
                        <input ref={billFileRef} type="file" accept="image/*,application/pdf" onChange={(e) => handleBillUpload(e, 'form')} className="hidden" />
                        <Button type="button" variant="outline" size="sm" onClick={() => billFileRef.current?.click()} disabled={billUploading} data-testid="upload-bill-btn">
                          {billUploading ? 'Uploading...' : <><Upload size={14} className="mr-1" /> {formData.bill_image_url ? 'Change' : 'Upload'}</>}
                        </Button>
                        {formData.bill_image_url && (
                          <a href={`${process.env.REACT_APP_BACKEND_URL}${formData.bill_image_url}`} target="_blank" rel="noreferrer" className="text-xs text-info underline flex items-center gap-1">
                            <Eye size={12} /> View Bill
                          </a>
                        )}
                      </div>
                    </div>

                    {/* WHT (Withholding Tax) integration — only on payments, not bills */}
                    {formType === 'payment' && (
                      <div className="md:col-span-2 rounded-lg border border-border bg-muted/20 p-3 space-y-2">
                        <label className="flex items-center gap-2 cursor-pointer select-none">
                          <input type="checkbox" checked={formData.apply_wht}
                            onChange={async (e) => {
                              const apply = e.target.checked;
                              setFormData({ ...formData, apply_wht: apply, wht_rate_id: '', wht_rate_value: 0 });
                              if (apply && whtRates.length === 0) {
                                try {
                                  const res = await api.get('/wht/rates');
                                  setWhtRates(res.data || []);
                                } catch { /* silent */ }
                              }
                            }}
                            data-testid="apply-wht-checkbox"
                            className="rounded border-border" />
                          <span className="text-sm font-medium text-foreground">Apply Withholding Tax (WHT)</span>
                          <span className="text-[11px] text-muted-foreground">— for non-resident services, royalties, etc.</span>
                        </label>
                        {formData.apply_wht && (
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-1">
                            <div>
                              <Label className="text-xs">WHT Rate</Label>
                              <Select value={formData.wht_rate_id}
                                onValueChange={(v) => {
                                  const r = whtRates.find(x => x.id === v);
                                  setFormData({ ...formData, wht_rate_id: v, wht_rate_value: r?.rate || 0 });
                                }}>
                                <SelectTrigger data-testid="wht-rate-picker"><SelectValue placeholder="Pick WHT rate" /></SelectTrigger>
                                <SelectContent className="max-h-[280px]">
                                  {whtRates.filter(r => r.rate > 0).map(r => (
                                    <SelectItem key={r.id} value={r.id}>
                                      {(r.country_code || '').toUpperCase()} · {r.name} — {r.rate}%
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            {parseFloat(formData.amount) > 0 && formData.wht_rate_value > 0 && (
                              <div className="rounded bg-rose-50 dark:bg-rose-900/20 border border-rose-200/60 dark:border-rose-800 p-2 text-[11px] space-y-0.5">
                                <div className="flex justify-between"><span className="text-muted-foreground">Gross:</span><span className="font-mono">{parseFloat(formData.amount).toFixed(2)}</span></div>
                                <div className="flex justify-between text-rose-600"><span>WHT @ {formData.wht_rate_value}%:</span><span className="font-mono font-semibold">- {(parseFloat(formData.amount) * formData.wht_rate_value / 100).toFixed(2)}</span></div>
                                <div className="flex justify-between font-semibold border-t border-rose-200/60 dark:border-rose-800 pt-0.5"><span>Net to supplier:</span><span className="font-mono">{(parseFloat(formData.amount) * (1 - formData.wht_rate_value / 100)).toFixed(2)}</span></div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="flex gap-3 mt-2">
                    <Button type="submit" data-testid="submit-form-button" disabled={submitting}
                      className={`rounded-xl ${formType === 'bill' ? 'bg-warning hover:bg-warning' : ''}`}>
                      {submitting ? 'Saving...' : formType === 'bill' ? <><Receipt size={16} className="mr-1" /> Add Purchase Bill</> : <><DollarSign size={16} className="mr-1" /> Record Payment</>}
                    </Button>
                    <Button type="button" variant="outline" onClick={() => { setShowForm(false); resetForm(); }} className="rounded-xl">Cancel</Button>
                  </div>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Return Form Card */}
        {showReturnForm && (
          <Card className="border-2 border-error/30" data-testid="return-form-card">
            <CardHeader className="pb-3">
              <CardTitle className="font-outfit flex items-center gap-2">
                <RotateCcw className="text-error" size={20} /> Supplier Return
              </CardTitle>
              <p className="text-sm text-error bg-error/10 p-2 rounded-lg border border-error/30 mt-2">
                Record goods returned to a supplier. Choose: <strong>Cash Refund</strong> (get money back), <strong>Credit Return</strong> (reduce balance owed), or <strong>Full Invoice Return</strong> (return entire invoice).
              </p>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleReturnSubmit}>
                <div className="space-y-4">
                  <div>
                    <Label>Supplier *</Label>
                    <SearchableSelect
                      items={suppliers}
                      value={returnData.supplier_id}
                      onChange={(id) => setReturnData({ ...returnData, supplier_id: id })}
                      placeholder="Search supplier by name..."
                      className="mt-2"
                    />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <Label>Return Type *</Label>
                      <Select value={returnData.return_type} onValueChange={(v) => setReturnData({ ...returnData, return_type: v })}>
                        <SelectTrigger data-testid="return-type-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="cash_refund">Cash Refund (get money back)</SelectItem>
                          <SelectItem value="credit_return">Credit Return (reduce balance)</SelectItem>
                          <SelectItem value="full_invoice_return">Full Invoice Return</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Amount (SR) *</Label>
                      <Input type="number" step="0.01" value={returnData.amount} data-testid="return-amount-input"
                        onChange={(e) => setReturnData({ ...returnData, amount: e.target.value })} required placeholder="0.00" className="text-lg font-bold" />
                    </div>
                    <div>
                      <Label>Date *</Label>
                      <Input type="date" value={returnData.date} onChange={(e) => setReturnData({ ...returnData, date: e.target.value })} required />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label>Invoice Reference</Label>
                      <Input value={returnData.invoice_ref} onChange={(e) => setReturnData({ ...returnData, invoice_ref: e.target.value })} placeholder="Invoice #1234" />
                    </div>
                    <div>
                      <Label>Reason</Label>
                      <Input value={returnData.reason} onChange={(e) => setReturnData({ ...returnData, reason: e.target.value })} placeholder="Damaged goods, wrong order, etc." />
                    </div>
                  </div>
                  <div>
                    <Label>Bill / Return Document</Label>
                    <div className="flex items-center gap-3 mt-1">
                      <input ref={returnBillRef} type="file" accept="image/*,application/pdf" onChange={(e) => handleBillUpload(e, 'return')} className="hidden" />
                      <Button type="button" variant="outline" size="sm" onClick={() => returnBillRef.current?.click()} disabled={billUploading}>
                        {billUploading ? 'Uploading...' : <><Upload size={14} className="mr-1" /> Upload</>}
                      </Button>
                      {returnData.bill_image_url && (
                        <a href={`${process.env.REACT_APP_BACKEND_URL}${returnData.bill_image_url}`} target="_blank" rel="noreferrer" className="text-xs text-info underline flex items-center gap-1">
                          <Eye size={12} /> View
                        </a>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <Button type="submit" className="rounded-xl bg-error hover:bg-error" data-testid="submit-return-button" disabled={submitting}>
                      {submitting ? 'Saving...' : <><RotateCcw size={16} className="mr-1" /> Record Return</>}
                    </Button>
                    <Button type="button" variant="outline" onClick={() => setShowReturnForm(false)} className="rounded-xl">Cancel</Button>
                  </div>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Returns History */}
        {filteredReturns.length > 0 && (
          <Card className="border-error/30">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="font-outfit text-error flex items-center gap-2"><RotateCcw size={18} /> Supplier Returns</CardTitle>
                <Badge variant="outline" className="text-xs text-error">{filteredReturns.length} returns</Badge>
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="returns-table">
                  <thead><tr className="border-b bg-error/10">
                    <th className="text-left p-3 text-sm font-medium">Date</th>
                    <th className="text-left p-3 text-sm font-medium">Supplier</th>
                    <th className="text-left p-3 text-sm font-medium">Type</th>
                    <th className="text-right p-3 text-sm font-medium">Amount</th>
                    <th className="text-left p-3 text-sm font-medium">Invoice</th>
                    <th className="text-left p-3 text-sm font-medium">Reason</th>
                    <th className="text-right p-3 text-sm font-medium">Actions</th>
                  </tr></thead>
                  <tbody>
                    {filteredReturns.map(r => (
                      <tr key={r.id} className="border-b hover:bg-error/10">
                        <td className="p-3 text-sm">{r.date ? format(new Date(r.date), 'MMM dd, yyyy') : '-'}</td>
                        <td className="p-3 text-sm font-medium">{r.supplier_name}</td>
                        <td className="p-3"><Badge variant="outline" className="text-xs capitalize bg-error/10 text-error">{r.return_type?.replace('_', ' ')}</Badge></td>
                        <td className="p-3 text-sm text-right font-bold text-error">{cur()} {r.amount?.toFixed(2)}</td>
                        <td className="p-3 text-sm">{r.invoice_ref || '-'}</td>
                        <td className="p-3 text-sm text-muted-foreground truncate max-w-[150px]">{r.reason || '-'}</td>
                        <td className="p-3 text-right flex gap-1 justify-end">
                          {r.bill_image_url && (
                            <a href={`${process.env.REACT_APP_BACKEND_URL}${r.bill_image_url}`} target="_blank" rel="noreferrer">
                              <Button size="sm" variant="ghost" className="h-7"><Eye size={12} /></Button>
                            </a>
                          )}
                          <Button size="sm" variant="ghost" onClick={() => handleDeleteReturn(r.id)} className="h-7 text-error"><Trash2 size={12} /></Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Payments Table */}
        <Card className="border-border">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="font-outfit">All Supplier Transactions</CardTitle>
              <Badge variant="outline" className="text-xs">{filteredPayments.length} records</Badge>
            </div>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="payments-table">
                <thead>
                  <tr className="border-b border-border bg-muted/50">
                    <th className="text-left p-3 font-medium text-sm">Date</th>
                    <th className="text-left p-3 font-medium text-sm">Supplier</th>
                    <th className="text-left p-3 font-medium text-sm hidden md:table-cell">Paid By</th>
                    <th className="text-left p-3 font-medium text-sm hidden md:table-cell">Expense For</th>
                    <th className="text-right p-3 font-medium text-sm">Amount</th>
                    <th className="text-left p-3 font-medium text-sm">Mode</th>
                    <th className="text-left p-3 font-medium text-sm hidden lg:table-cell">Notes</th>
                    <th className="text-center p-3 font-medium text-sm hidden md:table-cell">Bill</th>
                    <th className="text-right p-3 font-medium text-sm">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredPayments.map((payment) => {
                    const branchName = branches.find(b => b.id === payment.branch_id)?.name || '-';
                    const expForName = payment.expense_for_branch_id ? branches.find(b => b.id === payment.expense_for_branch_id)?.name : null;
                    return (
                      <tr key={payment.id} className={`border-b border-border hover:bg-secondary/50 ${spDuplicateIds.has(payment.id) ? 'bg-orange-50/80 border-l-4 border-l-orange-400' : ''}`} data-testid={`payment-row-${payment.id}`}>
                        <td className="p-3 text-sm">
                          <div className="flex items-center gap-1">
                            {format(new Date(payment.date), 'MMM dd, yyyy')}
                            {spDuplicateIds.has(payment.id) && (
                              <span className="inline-flex items-center gap-0.5 px-1 py-0.5 rounded bg-orange-100 text-orange-700 text-[9px] font-bold border border-orange-300" data-testid={`sp-duplicate-badge-${payment.id}`}>
                                <Copy size={8} /> Duplicate
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="p-3 text-sm font-medium" data-testid={`supplier-name-cell-${payment.id}`}>
                          {payment.supplier_name ? (
                            payment.supplier_name
                          ) : (
                            <button
                              type="button"
                              onClick={() => { setReassignFor(payment); setReassignTarget(''); }}
                              className="inline-flex items-center gap-1 text-amber-700 italic underline-offset-2 hover:underline hover:text-amber-900 transition-colors"
                              title="Click to pick the correct supplier and re-stamp this row"
                              data-testid={`reassign-supplier-${payment.id}`}
                            >
                              <AlertTriangle size={12} /> — (unknown) <span className="not-italic text-[10px] font-medium bg-amber-100 px-1.5 py-0.5 rounded">fix</span>
                            </button>
                          )}
                        </td>
                        <td className="p-3 text-sm text-muted-foreground hidden md:table-cell">{branchName}</td>
                        <td className="p-3 text-sm hidden md:table-cell">{expForName ? <Badge variant="outline" className="bg-warning/10 border-warning/30 text-warning text-[10px]">{expForName}</Badge> : <span className="text-muted-foreground">-</span>}</td>
                        <td className="p-3 text-sm text-right font-bold">{cur()} {payment.amount.toFixed(2)}</td>
                        <td className="p-3">
                          {(() => {
                            // iter587 — Resilient mode rendering. Legacy
                            // banking-links rows had no `payment_mode`
                            // field (only `payment_method` + `type`).
                            // Derive a meaningful label so the pill is
                            // never blank.
                            const raw = (payment.payment_mode || payment.payment_method || '').toString().toLowerCase().trim();
                            const isBank = raw === 'bank' || raw.includes('bank') || raw === 'wire' || raw === 'online';
                            const isCash = raw === 'cash';
                            const isCredit = raw === 'credit';
                            const label = isCredit ? 'On Credit' : isCash ? 'cash' : isBank ? 'bank' : (raw || 'unknown');
                            const cls = isCash ? 'bg-success/10 text-success border-success/30'
                              : isBank ? 'bg-info/10 text-info border-info/30'
                              : isCredit ? 'bg-warning/10 text-warning border-warning/30'
                              : 'bg-stone-100 text-stone-500 border-stone-300';
                            return (
                              <Badge variant="outline" className={`text-xs ${cls}`} data-testid={`payment-mode-${payment.id}`}>
                                {isCash && <Banknote size={12} className="mr-1 inline" />}
                                {isBank && <CreditCard size={12} className="mr-1 inline" />}
                                {isCredit && <Receipt size={12} className="mr-1 inline" />}
                                {label}
                              </Badge>
                            );
                          })()}
                        </td>
                        <td className="p-3 text-sm text-muted-foreground max-w-[200px] truncate hidden lg:table-cell" title={payment.notes || payment.description || ''} data-testid={`payment-notes-${payment.id}`}>{payment.notes || payment.description || '-'}</td>
                        <td className="p-3 hidden md:table-cell">
                          {payment.bill_image_url ? (
                            <a href={`${process.env.REACT_APP_BACKEND_URL}${payment.bill_image_url}`} target="_blank" rel="noreferrer">
                              <Button size="sm" variant="ghost" className="h-7 text-info"><Eye size={12} className="mr-0.5" />Bill</Button>
                            </a>
                          ) : <span className="text-xs text-muted-foreground">-</span>}
                        </td>
                        <td className="p-3 text-right whitespace-nowrap">
                          <Button size="sm" variant="ghost" onClick={() => setJournalFor(payment)}
                            data-testid={`view-journal-${payment.id}`} className="h-8 mr-1 text-info">Journal</Button>
                          <Button size="sm" variant="outline" onClick={() => handleDelete(payment.id)}
                            data-testid="delete-payment-button" className="h-8 text-error hover:text-error hover:bg-error/10">
                            <Trash2 size={14} />
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                  {filteredPayments.length === 0 && (
                    <tr>
                      <td colSpan={8} className="p-8 text-center text-muted-foreground">
                        No supplier payments recorded yet. Use the buttons above to add a purchase bill or pay credit.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between mt-4 px-2">
                <span className="text-xs text-muted-foreground">{totalRecords} total records</span>
                <div className="flex items-center gap-2">
                  <Button size="sm" variant="outline" disabled={currentPage <= 1}
                    onClick={() => fetchData(currentPage - 1)} data-testid="prev-page">Previous</Button>
                  <span className="text-sm">Page {currentPage} of {totalPages}</span>
                  <Button size="sm" variant="outline" disabled={currentPage >= totalPages}
                    onClick={() => fetchData(currentPage + 1)} data-testid="next-page">Next</Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <SupplierPaymentJournalDialog payment={journalFor} onClose={() => setJournalFor(null)} onChanged={() => fetchData(currentPage)} />
      {/* Duplicate Warning Dialog */}
      <AlertDialog open={showDuplicateWarning} onOpenChange={setShowDuplicateWarning}>
        <AlertDialogContent data-testid="sp-duplicate-warning-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-orange-600">
              <AlertTriangle size={20} /> Possible Duplicate Entry
            </AlertDialogTitle>
            <AlertDialogDescription className="text-sm">
              <span className="font-bold text-orange-700">{duplicateCount}</span> payment(s) with the same supplier and amount
              (<span className="font-bold">{cur()} {parseFloat(formData.amount || 0).toFixed(2)}</span>) already exist on{' '}
              <span className="font-bold">{formData.date}</span>.
              <br /><br />
              Are you sure this is <span className="font-bold">not a duplicate</span> entry?
            </AlertDialogDescription>
            <DuplicatePeekList
              rows={duplicateExisting}
              kind="supplier_payment"
              onItemClick={(id) => {
                setShowDuplicateWarning(false);
                window.open(`/supplier-payments?focus=${encodeURIComponent(id)}`, '_blank', 'noopener');
              }}
            />
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-sp-duplicate-btn">Cancel & Review</AlertDialogCancel>
            <AlertDialogAction onClick={() => { setShowDuplicateWarning(false); pendingSubmitType === 'bill' ? submitBill() : submitPayment(); }} className="bg-orange-600 hover:bg-orange-700" data-testid="confirm-sp-duplicate-btn">
              Yes, Save Anyway
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* iter442e — Bank-statement picker */}
      <PickFromBankDialog
        open={showPickBank}
        onClose={() => setShowPickBank(false)}
        direction="outgoing"
        entityType={formData.supplier_id ? 'supplier' : null}
        entityId={formData.supplier_id || null}
        entityName={(suppliers.find(s => s.id === formData.supplier_id) || {}).name || ''}
        amountHint={formData.amount || null}
        onPick={(txn) => {
          setPickedBankTxn(txn);
          setFormData(fd => ({
            ...fd,
            amount: String(txn.amount),
            date: txn.date,
            notes: fd.notes || txn.description,
          }));
          toast.success('Bank line attached — review and Save');
        }}
      />

      {/* iter592 — Reassign supplier dialog for legacy "— (unknown)" rows */}
      <AlertDialog open={!!reassignFor} onOpenChange={(open) => { if (!open) { setReassignFor(null); setReassignTarget(''); } }}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-amber-700">
              <AlertTriangle size={20} /> Fix supplier on this row
            </AlertDialogTitle>
            <AlertDialogDescription className="text-sm">
              This payment of <span className="font-bold">{cur()} {Number(reassignFor?.amount || 0).toFixed(2)}</span>
              {reassignFor?.date && <> dated <span className="font-bold">{format(new Date(reassignFor.date), 'MMM dd, yyyy')}</span></>}
              {reassignFor?.notes && <> · <span className="italic text-muted-foreground">{String(reassignFor.notes).slice(0, 50)}{(reassignFor?.notes || '').length > 50 ? '…' : ''}</span></>}
              <br /><br />
              Pick the right supplier — current credit balances will be recalculated for both sides automatically.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-2">
            <SearchableSelect
              items={suppliers}
              value={reassignTarget}
              onChange={setReassignTarget}
              placeholder="Search supplier by name..."
              data-testid="reassign-supplier-picker"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-reassign-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleReassignSubmit}
              disabled={!reassignTarget || reassignSubmitting}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="confirm-reassign-btn"
            >
              {reassignSubmitting ? 'Saving...' : 'Reassign supplier'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardLayout>
  );
}
